import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, CalendarClock, Type, Compass, ChevronDown, ShieldCheck } from 'lucide-react';

const card = { backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border)' };
const kicker = 'text-xs tracking-[0.2em] uppercase mb-3';
const h2cls = 'font-heading text-3xl sm:text-4xl font-light tracking-tight mb-4';

export function Section({ id, label, children, className = '' }) {
  return (
    <section id={id} aria-label={label} className={`px-5 md:px-8 py-16 lg:py-20 ${className}`}>
      <div className="max-w-6xl mx-auto">{children}</div>
    </section>
  );
}

export function Hero({ c, onStart }) {
  return (
    <section className="relative px-5 md:px-8 pt-16 pb-14 lg:pt-24 lg:pb-20" id="main-content" aria-label="Hero">
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 20% 20%, rgba(192,130,97,0.10) 0%, transparent 55%)' }} aria-hidden="true" />
      <div className="max-w-6xl mx-auto relative">
        <div className="max-w-2xl">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight mb-6" style={{ color: 'var(--text-primary)' }} data-testid="home-hero-h1">{c.hero_h1}</h1>
          <p className="text-base lg:text-lg leading-relaxed mb-7" style={{ color: 'var(--text-secondary)' }} data-testid="home-hero-p">{c.hero_p}</p>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={onStart} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium text-white transition-transform hover:scale-[1.02]" style={{ backgroundColor: 'var(--accent)' }} data-testid="home-hero-start">{c.hero_primary} <ArrowRight className="w-4 h-4" aria-hidden="true" /></button>
            <Link to="/pricing" className="inline-flex items-center px-7 py-3.5 rounded-full text-sm font-medium" style={{ border: '1px solid var(--border-hover)', color: 'var(--text-primary)' }} data-testid="home-hero-memberships">{c.hero_secondary}</Link>
          </div>
          <p className="text-xs mt-4" style={{ color: 'var(--text-secondary)' }} data-testid="home-hero-notice">{c.hero_notice}</p>
        </div>
      </div>
    </section>
  );
}

export function ThreePaths({ c }) {
  const paths = [
    { id: 'free', t: c.path_free_t, p: c.path_free_p, to: '/pricing#free' },
    { id: 'grow', t: c.path_grow_t, p: c.path_grow_p, to: '/pricing' },
    { id: 'dating', t: c.path_dating_t, p: c.path_dating_p, to: '/dating' },
  ];
  return (
    <Section id="paths" label={c.paths_h2}>
      <p className={kicker} style={{ color: 'var(--accent)' }}>{c.paths_kicker}</p>
      <h2 className={h2cls} style={{ color: 'var(--text-primary)' }}>{c.paths_h2}</h2>
      <div className="grid md:grid-cols-3 gap-5 mt-8" data-testid="home-paths">
        {paths.map((p) => (
          <article key={p.id} className="rounded-2xl p-6 flex flex-col" style={{ ...card, ...(p.id === 'dating' ? { borderStyle: 'dashed' } : {}) }} data-testid={`home-path-${p.id}`}>
            <h3 className="font-heading text-2xl mb-2" style={{ color: 'var(--text-primary)' }}>{p.t}</h3>
            <p className="text-sm leading-relaxed flex-1" style={{ color: 'var(--text-secondary)' }}>{p.p}</p>
            <Link to={p.to} className="mt-5 inline-flex items-center gap-1 text-sm font-medium" style={{ color: 'var(--accent)' }} data-testid={`home-path-link-${p.id}`}>{c.path_link} <ArrowRight className="w-4 h-4" aria-hidden="true" /></Link>
          </article>
        ))}
      </div>
    </Section>
  );
}

export function FreeEssentials({ c }) {
  const tiles = [
    { id: 'sahi', icon: MessageCircle, t: c.tile_sahi_t, p: c.tile_sahi_p },
    { id: 'session', icon: CalendarClock, t: c.tile_session_t, p: c.tile_session_p },
    { id: 'name', icon: Type, t: c.tile_name_t, p: c.tile_name_p },
    { id: 'tools', icon: Compass, t: c.tile_tools_t, p: c.tile_tools_p },
  ];
  return (
    <Section id="free" label={c.free_h2} className="border-t" >
      <p className={kicker} style={{ color: 'var(--accent)' }}>{c.free_kicker}</p>
      <h2 className={h2cls} style={{ color: 'var(--text-primary)' }}>{c.free_h2}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="home-free-tiles">
        {tiles.map((tl) => (
          <Link key={tl.id} to="/pricing#free" className="rounded-2xl p-5 block transition-transform hover:-translate-y-0.5" style={card} data-testid={`home-tile-${tl.id}`}>
            <tl.icon className="w-5 h-5 mb-3" style={{ color: 'var(--accent)' }} aria-hidden="true" />
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{tl.t}</p>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{tl.p}</p>
          </Link>
        ))}
      </div>
      <p className="text-xs mt-4" style={{ color: 'var(--text-secondary)' }}><Link to="/pricing#free" className="underline" data-testid="home-free-details">{c.tile_link}</Link></p>
    </Section>
  );
}

export function HowItWorks({ c }) {
  const steps = [[c.how_1_t, c.how_1_p], [c.how_2_t, c.how_2_p], [c.how_3_t, c.how_3_p]];
  return (
    <Section id="how-it-works" label={c.how_h2} className="border-t">
      <p className={kicker} style={{ color: 'var(--accent)' }}>{c.how_kicker}</p>
      <h2 className={h2cls} style={{ color: 'var(--text-primary)' }}>{c.how_h2}</h2>
      <ol className="grid md:grid-cols-3 gap-5 mt-8" data-testid="home-how">
        {steps.map(([t, p], i) => (
          <li key={t} className="rounded-2xl p-6" style={card} data-testid={`home-how-${i + 1}`}>
            <p className="text-xs font-medium mb-2" style={{ color: 'var(--accent)' }}>0{i + 1}</p>
            <h3 className="text-lg font-heading mb-2" style={{ color: 'var(--text-primary)' }}>{t}</h3>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{p}</p>
          </li>
        ))}
      </ol>
    </Section>
  );
}

export function PrivacyScope({ c }) {
  return (
    <Section id="privacy" label={c.privacy_h2} className="border-t">
      <div className="max-w-3xl">
        <p className={kicker} style={{ color: 'var(--accent)' }}>{c.privacy_kicker}</p>
        <h2 className={h2cls} style={{ color: 'var(--text-primary)' }}><ShieldCheck className="inline w-7 h-7 mr-2 -mt-1" style={{ color: 'var(--accent)' }} aria-hidden="true" />{c.privacy_h2}</h2>
        <p className="text-sm lg:text-base leading-relaxed" style={{ color: 'var(--text-secondary)' }} data-testid="home-privacy-p">{c.privacy_p}</p>
        <p className="mt-4 text-sm"><Link to="/trust-safety" className="underline" style={{ color: 'var(--accent)' }} data-testid="home-privacy-link">{c.privacy_link}</Link></p>
      </div>
    </Section>
  );
}

export function Faq({ c }) {
  const [open, setOpen] = useState(0);
  return (
    <Section id="faq" label={c.faq_h2} className="border-t">
      <p className={kicker} style={{ color: 'var(--accent)' }}>{c.faq_kicker}</p>
      <h2 className={h2cls} style={{ color: 'var(--text-primary)' }}>{c.faq_h2}</h2>
      <div className="max-w-3xl divide-y mt-6" style={{ borderColor: 'var(--border)' }} data-testid="home-faq">
        {c.faq.map((f, i) => (
          <div key={f.q} className="py-3">
            <button type="button" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i} className="w-full flex items-center justify-between gap-4 text-left py-1" data-testid={`home-faq-q-${i}`}>
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{f.q}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${open === i ? 'rotate-180' : ''}`} style={{ color: 'var(--text-secondary)' }} aria-hidden="true" />
            </button>
            {open === i && <p className="text-sm leading-relaxed mt-2 pb-1" style={{ color: 'var(--text-secondary)' }} data-testid={`home-faq-a-${i}`}>{f.a}</p>}
          </div>
        ))}
      </div>
    </Section>
  );
}
